import { alpha, Badge, Box, IconButton, Typography, useTheme } from "@mui/material";
import { AI_Entity, Entity, Severity } from "../../../../constant/ApplicationVariables";
import { font } from "../../../../styles/font";
import { LightTooltip } from "../../../styled/CustomComponent";
import { AddOutlined, CheckOutlined, CloseOutlined, KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { useCallback, useEffect } from "react";

import { useApplicationContext } from "../../../../context/ApplicationContext";
import { addModule_API } from "../../../../api/controller/ModuleController";
import { getFolderStructureList_API } from "../../../../api/controller/RequirementController";
import { HttpStatusCode } from "axios";
import useErrorHandler from "../../../../api/errorHandler/useErrorHandler";
import { findFolder } from "../../../../utils/Folder";
import { FolderCustomIcon, SubFolderCustomIcon, TestCaseCustomIcon, UseCaseCustomIcon, UserStoryCustomIcon } from "../../../styled/CustomIcons";

export default function FolderNode({ node, style, dragHandle, selectedFolder, setSelectedFolder,  ROOT_SENTINEL, treeRef, inputRef, foldersList, setFoldersList, loading, setLoading, editNodeValue, handleGetSaveLocationDetails }) {
    const {
        activeProject,
        handleSnackbar,
        currentAIEntityData,
        currentDataSavingFolder
    } = useApplicationContext();

    const theme = useTheme();
    const errorHandler = useErrorHandler();

    const addFolderToTree = useCallback((list, parentId, newFolder) => {
        if (!Array.isArray(list)) return [newFolder];

        if (!parentId) {
            return [...list, {
                ...newFolder,
            }];
        }

        if (parentId && parentId === "__REACT_ARBORIST_INTERNAL_ROOT__") {
            return [...list, {
                ...newFolder,
            }];
        }

        return list.map(node => {
            if (node.id === parentId) {
                const childFolder = {
                    ...newFolder,

                };
                return {
                    ...node,
                    children: [
                        ...(node.children || []),
                        childFolder
                    ]
                };
            }
            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: addFolderToTree(node.children, parentId, newFolder)
                };
            }
            return node;
        });
    }, []);

    const handleAddFolder = useCallback(async (parentId) => {
        try {
            if (!parentId || !treeRef.current) {
                console.error('Invalid node or tree reference');
                return;
            }

            const parentNode = findFolder(foldersList, parentId);
            if (!parentNode) {
                console.error('Parent folder not found');
                return;
            }

            const folderName = prompt("Enter folder name:");
            if (!folderName?.trim()) return;

            const tempId = `folder-${Date.now()}`;
            const parentPath = parentNode.path || '';
            const sanitizedName = folderName.trim().toLowerCase().replace(/[^a-z0-9-_]/g, '_');
            const fullPath = parentPath ? `${parentPath}/${sanitizedName}` : sanitizedName;

            const newFolder = {
                id: tempId,
                name: folderName.trim(),
                path: fullPath,
                children: [],
                isFolder: true,
                userStoryCount: 0,
                useCaseCount: 0,
                testCaseCount: 0,
                parent: parentId,
                level: (parentNode.level || 0) + 1
            };

            // Update tree with optimistic UI
            treeRef.current.create({
                parentId: node.id,
                index: node.children?.length || 0,
                data: newFolder
            });

            // Update foldersList state
            setFoldersList(prev => addFolderToTree(prev, node.id, newFolder));

            // Focus and edit with proper timing
            requestAnimationFrame(() => {
                const newNode = treeRef.current?.get(tempId);
                if (newNode) {
                    newNode.edit();
                    // Focus input after DOM update
                    requestAnimationFrame(() => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                            inputRef.current.select();
                        }
                    });
                }
            });
        } catch (error) {
            console.error('Error adding folder:', error);
            handleSnackbar('Failed to add folder', Severity.error);

            // Cleanup on error
            if (treeRef.current) {
                treeRef.current.delete([`temp-${Date.now()}`]);
            }
        }
    }, [
        node.id,
        node.level,
        node.children?.length,
        node?.data?.children?.length,
        node?.data?.path,
        treeRef,
        inputRef,
        addFolderToTree,
        setFoldersList,
        handleSnackbar
    ]);

const handleFolderClick = (e, folder) => {
  e.stopPropagation();
  if (folder.isEditing) return;

  //  Pass the correct shape: { data: folder.data, node: folder }
  setSelectedFolder({
    data: folder.data,
    node: folder,
  });
};

    const handleIconClick = useCallback((e, folder) => {
        e.preventDefault();
        e.stopPropagation();
        if (!folder.isEditing) {
            folder.toggle();
        }
    }, []);

const handleRightClick = useCallback(
  (event, folder) => {
    event.preventDefault();
    event.stopPropagation();
    if (folder.isEditing) return;
    setSelectedFolder({
      data: folder.data,
      node: folder,
    });
  },
  [setSelectedFolder]
);

    const handleSubmitEdit2 = () => {
        const input = inputRef.current;
        console.log(input, "input, value", input?.value, node)

        if (input && input.value) {
            node.submit(input.value);
        }
    };

    const handleCancelEdit = () => {
        console.log(node, "cancel node")
        if (node.data.isTemp) {
            node.reset();
            treeRef.current?.delete([node.id]);
            editNodeValue.current = "";
        } else {
            node.reset();
            editNodeValue.current = "";
        }
    };

    const handleSubmitEditnew = async () => {
        try {
            const nodeName = editNodeValue.current || inputRef.current?.value || `New Folder ${(new Date().toLocaleTimeString()).slice(0, 5)}`;
            if (!nodeName || nodeName.length < 3) {
                handleSnackbar("Folder name must be at least 3 characters long", Severity.error);
                handleCancelEdit();
                return;
            }

            const body = {
                name: nodeName,
                projectName: activeProject?.name,
                section: currentAIEntityData?.entity === AI_Entity.userStory
                    ? Entity.userStory
                    : currentAIEntityData?.entity === AI_Entity.useCase
                        ? Entity.useCase
                        : Entity.testCase,
                path: node?.parent?.data?.path || node.data.path
            };

            const response = await addModule_API(body);
            let newFolder = response?.data || {};
            newFolder.children = Array.isArray(newFolder.children) ? newFolder.children : [];

            treeRef.current.delete([node.id]);

            treeRef.current.create({
                parentId: node.parent?.id || null,
                index: node.parent?.children?.length || 0,
                data: { ...newFolder }
            });

            node.submit(nodeName);
        } catch (error) {
            console.error('Folder update failed:', error);
            if (node.data.isTemp) {
                treeRef.current?.delete([node.id]);
            }
            handleSnackbar("Folder update failed", Severity.error);
        }
    };

    const handleSubmitEdit = async () => {
        try {
            if (!inputRef.current?.value && !editNodeValue.current) {
                handleCancelEdit();
            }

            const nodeName = editNodeValue.current || inputRef.current?.value || `New Folder ${(new Date().toLocaleTimeString()).slice(0, 5)}`;
            if (!nodeName || nodeName.length < 3) {
                handleSnackbar("Folder name must be at least 3 characters long", Severity.error);
                handleCancelEdit();
                return;
            }

            const body = {
                name: nodeName,
                projectName: activeProject?.name,
                section: currentAIEntityData?.entity === AI_Entity.userStory
                    ? Entity.userStory
                    : currentAIEntityData?.entity === AI_Entity.useCase
                        ? Entity.useCase
                        : Entity.testCase,
                path: node.data.path
            };

            const response = await addModule_API(body);

            if (response.status === HttpStatusCode.Ok) {
                let newFolder = response?.data || {};
                newFolder.children = Array.isArray(newFolder.children) ? newFolder.children : [];

                treeRef.current.delete([node.id]);

                treeRef.current.create({
                    parentId: node.parent?.id || null,
                    index: node.parent?.children?.length || 0,
                    data: { ...newFolder }
                });

                node.submit(inputRef.current?.value);
                editNodeValue.current = "";

                setFoldersList(prev => addFolderToTree(prev, node.parent?.id, newFolder));

                const newNode = treeRef.current?.get(newFolder?.id);
                if (newNode) {
                    setSelectedFolder(newNode);
                    newNode?.focus();
                    newNode?.select();
                    treeRef.current.scrollTo(newFolder?.id, { align: 'auto' });
                }
            }

        } catch (error) {
            console.error('Folder update failed:', error);
            if (node.data.isTemp) {
                node.submit(inputRef.current?.value);
                treeRef.current?.delete([node.id]);
            }
            throw error;
        }
    };

    const handleSubmitEditOld = async () => {
        try {
            if (!inputRef.current?.value && !editNodeValue.current) {
                handleCancelEdit();
            }

            const nodeName = editNodeValue.current || inputRef.current?.value || `New Folder ${(new Date().toLocaleTimeString()).slice(0, 5)}`;

            if (nodeName?.length < 3) {
                handleSnackbar("Folder name must be at least 3 characters long", "error");
                return;
                handleCancelEdit();
            }

            treeRef.current.create({
                parentId: null,
                index: 0,
                data: {
                    id: new Date().toISOString(),
                    name: "nodeName",
                    children: []
                }
            });

            const body = {
                name: nodeName,
                projectName: activeProject?.name,
                section: currentAIEntityData?.entity === AI_Entity.userStory
                    ? Entity.userStory
                    : currentAIEntityData?.entity === AI_Entity.useCase
                        ? Entity.useCase
                        : Entity.testCase,
                path: node?.parent?.data?.path || node.data.path
            };

            const response = await addModule_API(body);
            let newFolder = response?.data;

            node.submit(inputRef.current?.value);

            if (activeProject && currentAIEntityData?.entity && currentAIEntityData?.entity !== AI_Entity.folder) {
                handleGetFoldersList(activeProject, currentAIEntityData?.entity);
            }
        } catch (error) {
            console.error('Folder update failed:', error);
            if (node.data.isTemp) {
                treeRef.current?.delete([node.id]);
            }
            throw error;
        }
    };

    const handleCreateFolder = async (node) => {
        try {
            let nodeName = "New Folder " + new Date();

            const input = inputRef.current;
            if (input && input.value) {
                nodeName = input.value;
            }

            let body = {
                name: nodeName,
                projectName: activeProject?.name,
                section: currentAIEntityData?.entity === AI_Entity.userStory
                    ? Entity.userStory
                    : currentAIEntityData?.entity === AI_Entity.useCase
                        ? Entity.useCase
                        : Entity.testCase,
                path: node?.parent?.data?.path
            }

            const response = await addModule_API(body);

            console.log({
                id: node.id,
                data: {
                    name: body.name,
                    id: node?.id,
                    children: [],
                    isTemp: false,
                }
            }, "newfolderdata")

            treeRef.current.update({
                id: node.id,
                data: {
                    name: body.name,
                    id: node?.id,
                    children: [],
                    isTemp: false,
                }
            });

        } catch (error) {
            console.log('Folder creation failed:', error);
            throw error;
        }
    }

    const handleGetFoldersList = async (projectData, entity) => {
        try {
            setLoading(true)

            let projectName = projectData?.name;

            let body = {
                projectName: projectName,
                section: entity === AI_Entity.userStory ? Entity.userStory : entity === AI_Entity.useCase ? Entity.useCase : entity === AI_Entity.testCase ? Entity.testCase : Entity.testCase,
            }

            console.log(body, "bodyin handleGetFoldersList")

            const response = await getFolderStructureList_API(body);

            console.log(response, "reponse handleGetFoldersList")

            if (response) {
                errorHandler(response, setLoading);
            }

            if (response.status === HttpStatusCode.Ok) {
                setFoldersList(response.data);

                let folder = findFolder(response.data, "name", editNodeValue.current);
                if (folder) {
                    if (folder?.id === node?.id) {
                        setSelectedFolder(folder);
                    }
                }
            }

        } catch (error) {
            console.log(error, error?.message, "projectreponse errorr");
            handleSnackbar(String(error), Severity.error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (currentDataSavingFolder?.id === node?.id) {
            requestAnimationFrame(() => {
                node?.focus();
            });
        }
    }, [currentDataSavingFolder?.id, node]);

   useEffect(() => {
  if (
    editNodeValue.current &&
    node?.data?.name === editNodeValue.current &&
    selectedFolder?.data?.id !== node?.id
  ) {
    requestAnimationFrame(() => {
      node?.focus();
      node?.select();
      editNodeValue.current = "";
    });
  }
}, [node?.data?.name, node, editNodeValue, selectedFolder]);

    useEffect(() => {
        if (node.isEditing && inputRef.current) {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [node.isEditing, inputRef]);

    console.log(node, "nodeinfolder")
    console.log(selectedFolder, "selectedFolder node1data")

    // Check if current node is selected
 //  Safe selection check — handles ROOT_SENTINEL, .data wrapping, and raw objects
const isSelected = (() => {
  if (!selectedFolder) return false;
  if (selectedFolder === ROOT_SENTINEL || selectedFolder?.__isRoot) return false;
  // handle { data: {...}, node: {...} } shape
  if (selectedFolder?.data?.id) return selectedFolder.data.id === node?.id;
  // handle raw folder object
  if (selectedFolder?.id) return selectedFolder.id === node?.id;
  return false;
})();

//  Defensive highlight — only call if the function exists
const isHighlighted =
  typeof handleGetSaveLocationDetails === "function" &&
  handleGetSaveLocationDetails()?.value === node?.data?.path;

    return (
        <Box
            className={`flex-1 flex items-center gap-1 py-0 px-2 cursor-pointer`}
            borderRadius={theme.shape.borderRadius}
            sx={{
                backgroundColor: isSelected 
                    ? theme.palette.primary.main 
                    : isHighlighted 
                        ? theme.palette.action.hover 
                        : 'transparent',
                color: isSelected 
                    ? theme.palette.primary.contrastText 
                    : theme.palette.text.secondary,
                fontWeight: isSelected || isHighlighted ? 600 : 400,
                border: isHighlighted ? `1px solid ${theme.palette.divider}` : `1px solid transparent`,
                pl: node.level * 4,
                "&:hover": {
                    backgroundColor: !isSelected && theme.palette.action.hover,
                    color: !isSelected && theme.palette.text.primary,
                }
            }}
        >
            <Box className="" onClick={(e) => handleIconClick(e, node)}>
                {!node.isLeaf && (
                    node.isOpen ?
                        <KeyboardArrowDown sx={{ color: "inherit" }} /> :
                        <KeyboardArrowRight sx={{ color: "inherit" }} />
                )}
            </Box>

            <Box
                className="flex-1 flex items-center gap-1 relative"
                onClick={(e) => !node.isEditing && handleFolderClick(e, node)}
                sx={{
                    pl: 0,
                }}
                onContextMenu={(e) => !node.isEditing && handleRightClick(e, node)}
            >
                <FolderCustomIcon sx={{ color: "inherit" }} />

                {node.isEditing ? (
                    <div style={{ display: 'flex', width: '100%' }}>
                        <input
                            ref={inputRef}
                            defaultValue={editNodeValue.current || ""}
                            onChange={(e) => (editNodeValue.current = e.target.value)}
                            style={{
                                width: '100%',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: theme.shape.borderRadius,
                                padding: '0.25rem',
                                outline: 'none',
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                            }}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSubmitEdit();
                                if (e.key === 'Escape') handleCancelEdit();
                            }}
                        />
                        <div style={{ display: 'flex', marginLeft: '8px' }}>
                            <IconButton 
                                onClick={handleSubmitEdit}
                                sx={{ 
                                    color: theme.palette.success.main,
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.success.main, 0.1)
                                    }
                                }}
                            >
                                <CheckOutlined fontSize="small" />
                            </IconButton>
                            <IconButton 
                                onClick={handleCancelEdit}
                                sx={{ 
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.error.main, 0.1)
                                    }
                                }}
                            >
                                <CloseOutlined fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                ) : (
                    <>
                        <LightTooltip
                            title={
                                <Box className="flex flex-col gap-1">
                                    <Typography fontSize={font.size.fs_5} className="flex-1">
                                        Title: {node.data.name}
                                    </Typography>
                                    
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Badge
                                            badgeContent={node?.data?.children?.length}
                                            color="secondary"
                                        >
                                            <SubFolderCustomIcon />
                                        </Badge>
                                    </Box>

                                    {currentAIEntityData?.entity === AI_Entity.userStory &&
                                        <Typography fontSize={font.size.fs_5}>
                                            User Story: {node?.data?.userStoryCount}
                                        </Typography>
                                    }
                                    {currentAIEntityData?.entity === AI_Entity.useCase &&
                                        <Typography fontSize={font.size.fs_5}>
                                            Use Case: {node?.data?.useCaseCount}
                                        </Typography>
                                    }
                                    {currentAIEntityData?.entity === AI_Entity.testCase &&
                                        <Typography fontSize={font.size.fs_5}>
                                            Test Case: {node?.data?.testCaseCount}
                                        </Typography>
                                    }
                                </Box>
                            }
                            placement="right"
                        >
                            <Box className="flex-1 flex items-center gap-2 box-border">
                                <Typography 
                                    color="inherit" 
                                    className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap box-border"
                                >
                                    {node?.data?.name}
                                </Typography>

                                <Badge
                                    badgeContent={node?.data?.children?.length}
                                    color="secondary"
                                    sx={{
                                        color: isSelected 
                                            ? theme.palette.primary.contrastText 
                                            : theme.palette.text.secondary,
                                        '& .MuiBadge-badge': {
                                            fontSize: font.size.fs_6,
                                            width: 10,
                                            height: 10,
                                        },
                                    }}
                                >
                                    <SubFolderCustomIcon />
                                </Badge>

                                <Badge
                                    badgeContent={
                                        currentAIEntityData?.entity === AI_Entity.userStory
                                            ? node?.data?.userStoryCount
                                            : currentAIEntityData?.entity === AI_Entity.useCase
                                                ? node?.data?.useCaseCount
                                                : currentAIEntityData?.entity === AI_Entity.testCase
                                                    ? node?.data?.testCaseCount
                                                    : 0
                                    }
                                    color="secondary"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            fontSize: font.size.fs_6,
                                            width: 10,
                                            height: 10,
                                        },
                                        color: isSelected 
                                            ? theme.palette.primary.contrastText 
                                            : theme.palette.text.secondary,
                                    }}
                                >
                                    {currentAIEntityData?.entity === AI_Entity.userStory && <UserStoryCustomIcon />}
                                    {currentAIEntityData?.entity === AI_Entity.useCase && <UseCaseCustomIcon />}
                                    {currentAIEntityData?.entity === AI_Entity.testCase && <TestCaseCustomIcon />}
                                </Badge>

                                <IconButton 
                                    sx={{ 
                                        fontSize: font.size.fs_5,
                                        color: isSelected 
                                            ? theme.palette.primary.contrastText 
                                            : theme.palette.text.secondary,
                                        '&:hover': {
                                            backgroundColor: isSelected 
                                                ? alpha(theme.palette.primary.contrastText, 0.1)
                                                : alpha(theme.palette.primary.main, 0.1)
                                        }
                                    }} 
                                    disabled 
                                    onClick={() => handleAddFolder()}
                                >
                                    <AddOutlined />
                                </IconButton>
                            </Box>
                        </LightTooltip>
                    </>
                )}
            </Box>
        </Box>
    );
}